import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { surveyAPI, referenceAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { sumOthersLineAmounts } from '../utils/invoiceFinancialCalculations'

const SurveyForm = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { isAuthenticated, loading: authLoading, isSuperuser, userGroups, isFinanceManager } = useAuth()
  // Direct group-based and user-based access control
  const isAdmin = isSuperuser || userGroups.includes('Admin')
  const hasFullAccess = isSuperuser || isAdmin || isFinanceManager
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({})
  const [countries, setCountries] = useState([])
  const [ports, setPorts] = useState([])
  const [surveyTypes, setSurveyTypes] = useState([])
  const [surveyors, setSurveyors] = useState([])
  const [clients, setClients] = useState([])
  const [pctDisabled, setPctDisabled] = useState(false)
  const [mtPriceDisabled, setMtPriceDisabled] = useState(false)
  /** Mirrors invoice invoice_meta.extra_lines; rolled into Survey.others on save */
  const [othersLines, setOthersLines] = useState([])

  // Format date for display in European format
  const formatDisplayDate = (dateString) => {
    if (!dateString) return ''
    // Parse the date string as UTC to avoid timezone shifts
    const [year, month, day] = dateString.split('-').map(Number)
    const date = new Date(Date.UTC(year, month - 1, day))
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      timeZone: 'UTC'
    })
  }

  // Custom date input component that shows European format
  const CustomDateInput = ({ name, value, onChange, required, id, className }) => {
    const [displayValue, setDisplayValue] = useState('')
    const [isFocused, setIsFocused] = useState(false)

    useEffect(() => {
      if (value && !isFocused) {
        setDisplayValue(formatDisplayDate(value))
      } else if (!value) {
        setDisplayValue('')
      }
    }, [value, isFocused])

    const handleFocus = () => {
      setIsFocused(true)
      // Show the actual date in YYYY-MM-DD format when focused
      setDisplayValue(value || '')
    }

    const handleBlur = () => {
      setIsFocused(false)
      // Show formatted date when not focused
      setDisplayValue(formatDisplayDate(value))
    }

    const handleChange = (e) => {
      const newValue = e.target.value
      setDisplayValue(newValue)
      onChange(e)
    }

    return (
      <div className="relative">
        <input
          type="date"
          name={name}
          id={id}
          value={value || ''}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={className}
          required={required}
          style={{ 
            position: 'absolute',
            opacity: 0,
            width: '100%',
            height: '100%',
            cursor: 'pointer',
            zIndex: 2
          }}
        />
        <input
          type="text"
          value={displayValue}
          readOnly
          placeholder="DD Jan YYYY"
          className={`${className} cursor-pointer`}
          style={{ 
            position: 'relative',
            zIndex: 1,
            backgroundColor: 'white'
          }}
          onClick={() => document.getElementById(id)?.focus()}
        />
      </div>
    )
  }

  useEffect(() => {
    // Wait for authentication check to complete before loading data
    if (!authLoading && isAuthenticated) {
      loadReferenceData()
      if (id) {
        loadSurvey()
      } else {
        loadNextReference()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, authLoading, isAuthenticated])

  /** Keep Others = sum(extra_lines); totals follow sub-lines only */
  useEffect(() => {
    if (!id || !hasFullAccess) return
    const sumStr = sumOthersLineAmounts(othersLines).toFixed(2)
    setFormData((prev) => {
      if (String(prev.others ?? '') === sumStr) return prev
      const recovery = parseFloat(prev.recovery || 0)
      const surveyFee = parseFloat(prev.survey_fee || 0)
      const expenses = parseFloat(prev.expenses || 0)
      const others = parseFloat(sumStr || 0)
      const discount = parseFloat(prev.discount || 0)
      return {
        ...prev,
        others: sumStr,
        total: (recovery + surveyFee + expenses + others - discount).toFixed(2),
      }
    })
  }, [othersLines, id, hasFullAccess])

  const loadReferenceData = async () => {
    try {
      const [countriesRes, typesRes, clientsRes] = await Promise.all([
        referenceAPI.getCountries(),
        referenceAPI.getSurveyTypes(),
        referenceAPI.getClients(),
      ])
      setCountries(countriesRes.data.countries || [])
      setSurveyTypes(typesRes.data.survey_types || [])
      setClients(clientsRes.data.clients || [])
    } catch (error) {
      console.error('Error loading reference data:', error)
      if (error.response?.status === 401 || error.response?.status === 403) {
        // Authentication error - will be handled by interceptor
        return
      }
    }
  }

  const loadSurvey = async () => {
    try {
      const response = await surveyAPI.get(id)
      const row = response.data
      setFormData(row)
      const meta = row.invoice_meta && typeof row.invoice_meta === 'object' ? row.invoice_meta : {}
      const raw = Array.isArray(meta.extra_lines) ? meta.extra_lines : []
      let othersLineRows = raw.map((r, idx) => ({
        id: r.id != null ? r.id : `survey-others-${idx}-${Date.now()}`,
        description: r.description != null ? String(r.description) : '',
        amount: r.amount != null ? String(r.amount) : '0.00',
      }))
      if (othersLineRows.length === 0) {
        const legacyOthers = parseFloat(row.others)
        if (Number.isFinite(legacyOthers) && legacyOthers !== 0) {
          othersLineRows = [
            {
              id: `survey-legacy-others-${Date.now()}`,
              description: '',
              amount: legacyOthers.toFixed(2),
            },
          ]
        }
      }
      setOthersLines(othersLineRows)
      // Initialize disabled states based on loaded data
      if (response.data.mt_price && response.data.mt_price !== '') {
        setPctDisabled(true)
        setMtPriceDisabled(false)
      } else if (response.data.pct_qty && response.data.pct_qty !== '') {
        setMtPriceDisabled(true)
        setPctDisabled(false)
      } else {
        setPctDisabled(false)
        setMtPriceDisabled(false)
      }
      if (response.data.survey_country) {
        loadPorts(response.data.survey_country)
      }
      if (response.data.surveyor_type) {
        loadSurveyors(response.data.surveyor_type)
      }
    } catch (error) {
      console.error('Error loading survey:', error)
    }
  }

  const loadNextReference = async () => {
    // Automatically query database to determine next sequential reference number
    // This queries all surveys for the current year and generates the next number
    try {
      const response = await surveyAPI.generateReference()
      setFormData(prev => ({ ...prev, reference_number: response.data.reference_number }))
    } catch (error) {
      console.error('Error loading next reference:', error)
      if (error.response?.status === 401 || error.response?.status === 403) {
        // Authentication error - will be handled by interceptor
        return
      }
      alert('Error generating reference number. Please refresh the page.')
    }
  }

  const loadPorts = async (country) => {
    try {
      const response = await referenceAPI.getPorts(country)
      setPorts(response.data.ports || [])
    } catch (error) {
      console.error('Error loading ports:', error)
    }
  }

  const loadSurveyors = async (surveyorType) => {
    try {
      if (surveyorType) {
        const response = await referenceAPI.getSurveyorsByType(surveyorType)
        setSurveyors(response.data.surveyors || [])
      } else {
        setSurveyors([])
      }
    } catch (error) {
      console.error('Error loading surveyors:', error)
      setSurveyors([])
    }
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    const inputValue = type === 'checkbox' ? checked : value
    
    setFormData(prev => {
      const newData = { ...prev, [name]: inputValue }
      
      // Calculate fuel differences when logbook or survey values change
      if (name === 'lsfo_logbook' || name === 'lsfo_survey') {
        const logbook = parseFloat(name === 'lsfo_logbook' ? inputValue : prev.lsfo_logbook) || 0
        const survey = parseFloat(name === 'lsfo_survey' ? inputValue : prev.lsfo_survey) || 0
        newData.lsfo_qty = (survey - logbook).toFixed(3)
      }
      if (name === 'hsfo_logbook' || name === 'hsfo_survey') {
        const logbook = parseFloat(name === 'hsfo_logbook' ? inputValue : prev.hsfo_logbook) || 0
        const survey = parseFloat(name === 'hsfo_survey' ? inputValue : prev.hsfo_survey) || 0
        newData.hsfo_qty = (survey - logbook).toFixed(3)
      }
      if (name === 'mgo_logbook' || name === 'mgo_survey') {
        const logbook = parseFloat(name === 'mgo_logbook' ? inputValue : prev.mgo_logbook) || 0
        const survey = parseFloat(name === 'mgo_survey' ? inputValue : prev.mgo_survey) || 0
        newData.mgo_qty = (survey - logbook).toFixed(3)
      }
      
      // Calculate fuel recoveries for superusers and admins
      if (hasFullAccess && id) {
        // Handle MT Price logic - when MT Price is entered
        if (name === 'mt_price') {
          const mtValue = inputValue.trim()
          if (mtValue !== '') {
            // Clear and disable PCT
            newData.pct_qty = ''
            setPctDisabled(true)
            setMtPriceDisabled(false)
            // Do not modify fuel prices - they are now independent
            // Recalculate fuel recoveries with existing individual prices
            const lsfoQty = parseFloat(newData.lsfo_qty || 0)
            const hsfoQty = parseFloat(newData.hsfo_qty || 0)
            const mgoQty = parseFloat(newData.mgo_qty || 0)
            const lsfoPrice = parseFloat(newData.lsfo_price || 0)
            const hsfoPrice = parseFloat(newData.hsfo_price || 0)
            const mgoPrice = parseFloat(newData.mgo_price || 0)
            newData.lsfo_recovery = (lsfoQty * lsfoPrice).toFixed(2)
            newData.hsfo_recovery = (hsfoQty * hsfoPrice).toFixed(2)
            newData.mgo_recovery = (mgoQty * mgoPrice).toFixed(2)
          } else {
            // When MT Price is completely cleared
            setPctDisabled(false)
            setMtPriceDisabled(false)
            // Do not clear fuel prices - they are independent
            // Recalculate fuel recoveries with existing individual prices
            const lsfoQty = parseFloat(newData.lsfo_qty || 0)
            const hsfoQty = parseFloat(newData.hsfo_qty || 0)
            const mgoQty = parseFloat(newData.mgo_qty || 0)
            const lsfoPrice = parseFloat(newData.lsfo_price || 0)
            const hsfoPrice = parseFloat(newData.hsfo_price || 0)
            const mgoPrice = parseFloat(newData.mgo_price || 0)
            newData.lsfo_recovery = (lsfoQty * lsfoPrice).toFixed(2)
            newData.hsfo_recovery = (hsfoQty * hsfoPrice).toFixed(2)
            newData.mgo_recovery = (mgoQty * mgoPrice).toFixed(2)
          }
        }
        
        // Handle PCT Difference logic - when PCT is entered
        if (name === 'pct_qty') {
          const pctValue = inputValue.trim()
          if (pctValue !== '') {
            // Clear MT Price when PCT is entered
            newData.mt_price = ''
            setMtPriceDisabled(true)
            setPctDisabled(false)
            // Do not modify fuel prices when using PCT
          } else {
            // When PCT is cleared
            setMtPriceDisabled(false)
            setPctDisabled(false)
          }
        }
        
        // Calculate individual fuel recoveries - always recalculate when qty or price changes
        // This matches backend behavior: recovery = qty * price
        if (name === 'lsfo_logbook' || name === 'lsfo_survey' || name === 'lsfo_qty' || name === 'lsfo_price') {
          const qty = parseFloat(newData.lsfo_qty || 0)
          const price = parseFloat(newData.lsfo_price || 0)
          newData.lsfo_recovery = (qty * price).toFixed(2)
        }
        if (name === 'hsfo_logbook' || name === 'hsfo_survey' || name === 'hsfo_qty' || name === 'hsfo_price') {
          const qty = parseFloat(newData.hsfo_qty || 0)
          const price = parseFloat(newData.hsfo_price || 0)
          newData.hsfo_recovery = (qty * price).toFixed(2)
        }
        if (name === 'mgo_logbook' || name === 'mgo_survey' || name === 'mgo_qty' || name === 'mgo_price') {
          const qty = parseFloat(newData.mgo_qty || 0)
          const price = parseFloat(newData.mgo_price || 0)
          newData.mgo_recovery = (qty * price).toFixed(2)
        }
        
        // Handle individual fuel price changes - prices are now independent from MT Price
        // No need to clear MT Price when individual prices change
        
        // Calculate recovery (incentive) based on MT Price or PCT
        if (name === 'mt_price' || name === 'pct_qty' || name === 'lsfo_qty' || name === 'hsfo_qty' || name === 'mgo_qty' || 
            name === 'lsfo_price' || name === 'hsfo_price' || name === 'mgo_price' || 
            name === 'lsfo_recovery' || name === 'hsfo_recovery' || name === 'mgo_recovery' ||
            name === 'lsfo_logbook' || name === 'lsfo_survey' || 
            name === 'hsfo_logbook' || name === 'hsfo_survey' ||
            name === 'mgo_logbook' || name === 'mgo_survey') {
          
          const mtPrice = parseFloat(newData.mt_price || 0)
          const pctDiff = parseFloat(newData.pct_qty || 0)
          
          // Clear recovery if both PCT and MT Price are empty
          if (!mtPrice && !pctDiff) {
            newData.recovery = ''
          } else if (mtPrice > 0) {
            // Calculate using MT Price: sum of positive differences * MT Price
            const lsfoDiff = parseFloat(newData.lsfo_qty || 0)
            const hsfoDiff = parseFloat(newData.hsfo_qty || 0)
            const mgoDiff = parseFloat(newData.mgo_qty || 0)
            
            let totalDiff = 0
            if (lsfoDiff > 0) totalDiff += lsfoDiff
            if (hsfoDiff > 0) totalDiff += hsfoDiff
            if (mgoDiff > 0) totalDiff += mgoDiff
            
            newData.recovery = (totalDiff * mtPrice).toFixed(2)
          } else if (pctDiff > 0) {
            // Calculate using PCT: sum of positive recoveries * PCT * 0.01
            const lsfoRecovery = parseFloat(newData.lsfo_recovery || 0)
            const hsfoRecovery = parseFloat(newData.hsfo_recovery || 0)
            const mgoRecovery = parseFloat(newData.mgo_recovery || 0)
            
            let totalRecovery = 0
            if (lsfoRecovery > 0) totalRecovery += lsfoRecovery
            if (hsfoRecovery > 0) totalRecovery += hsfoRecovery
            if (mgoRecovery > 0) totalRecovery += mgoRecovery
            
            newData.recovery = (totalRecovery * pctDiff * 0.01).toFixed(2)
          }
        }
        
        // Calculate total when financial fields change
        if (['recovery', 'survey_fee', 'expenses', 'others', 'discount'].includes(name)) {
          const recovery = parseFloat(newData.recovery || 0)
          const surveyFee = parseFloat(newData.survey_fee || 0)
          const expenses = parseFloat(newData.expenses || 0)
          const others = parseFloat(newData.others || 0)
          const discount = parseFloat(newData.discount || 0)
          newData.total = (recovery + surveyFee + expenses + others - discount).toFixed(2)
        }
      }
      
      return newData
    })
    
    if (name === 'survey_country') {
      setPorts([])
      if (value) {
        loadPorts(value)
      }
    }
    
    if (name === 'surveyor_type') {
      setSurveyors([])
      setFormData(prev => ({ ...prev, surveyor: '' })) // Clear surveyor when type changes
      if (value) {
        loadSurveyors(value)
      }
    }
  }

  const addOthersSurveyLine = () => {
    setOthersLines((prev) => {
      const newId = Date.now()
      if (prev.length > 0) {
        return [...prev, { id: newId, description: '', amount: '0.00' }]
      }
      const seeded =
        typeof formData.others !== 'undefined' && formData.others !== ''
          ? parseFloat(formData.others)
          : 0
      return [
        {
          id: newId,
          description: '',
          amount: Number.isFinite(seeded) && seeded > 0 ? seeded.toFixed(2) : '0.00',
        },
      ]
    })
  }

  const removeOthersSurveyLine = (lineId) => {
    setOthersLines((prev) => prev.filter((row) => row.id !== lineId))
  }

  const updateOthersLine = (lineId, field, raw) => {
    setOthersLines((prev) =>
      prev.map((row) => (row.id === lineId ? { ...row, [field]: raw } : row))
    )
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    
    try {
      // Prepare form data - convert empty strings to null for optional fields
      const submitData = { ...formData }
      
      // Clear disabled fields before submit (ensure they're empty)
      if (pctDisabled) {
        submitData.pct_qty = null
      }
      if (mtPriceDisabled) {
        submitData.mt_price = null
      }
      
      // Convert empty strings to null for optional fields that might cause issues
      if (submitData.surveyor === '') {
        submitData.surveyor = null
      }
      if (submitData.notes === '') {
        submitData.notes = null
      }

      if (id && hasFullAccess) {
        const curMeta =
          submitData.invoice_meta && typeof submitData.invoice_meta === 'object'
            ? submitData.invoice_meta
            : {}
        submitData.invoice_meta = {
          ...curMeta,
          extra_lines: othersLines.map(({ id: lid, description, amount }) => {
            const prevLine = Array.isArray(curMeta.extra_lines)
              ? curMeta.extra_lines.find((r) => r.id === lid)
              : null
            return {
              id: lid,
              description,
              amount,
              selected: prevLine?.selected !== false,
            }
          }),
        }
      }
      
      if (id) {
        await surveyAPI.update(id, submitData)
      } else {
        // For new surveys, reference number is automatically generated by backend
        // even if one is provided in formData, backend will override it
        await surveyAPI.create(submitData)
      }
      navigate('/surveys')
    } catch (error) {
      console.error('Error saving survey:', error)
      
      // Extract detailed error messages
      let errorMessage = 'Error saving survey: '
      
      if (error.response?.data) {
        const errorData = error.response.data
        
        // Check for validation errors
        if (errorData.errors) {
          const errorMessages = []
          for (const [field, messages] of Object.entries(errorData.errors)) {
            if (Array.isArray(messages)) {
              errorMessages.push(`${field}: ${messages.join(', ')}`)
            } else {
              errorMessages.push(`${field}: ${messages}`)
            }
          }
          errorMessage += errorMessages.join('; ')
        } else if (errorData.message) {
          errorMessage += errorData.message
        } else {
          errorMessage += JSON.stringify(errorData)
        }
      } else {
        errorMessage += error.message
      }
      
      alert(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold">
            <span className="text-premium">{id ? 'Edit Survey' : 'New Survey'}</span>
          </h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">
            {id ? 'Update survey information' : 'Create a new survey record'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-[#d9e2f1] shadow-[0_1px_6px_rgba(1,109,247,0.12)] overflow-hidden">
          {/* Main Form Content */}
          <div className="p-6 space-y-7">
            {/* Basic Information Section */}
            <div>
              <div className="mb-4 flex items-center gap-2.5 border-b border-[#e5ebf5] pb-2">
                <span className="h-4 w-1 rounded-full bg-premium"></span>
                <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-800">Basic Information</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <img 
                      src={`${process.env.PUBLIC_URL || ''}/images/logo_shape.png`}
                      alt="JNJ Logo" 
                      className="w-6 h-6 object-contain"
                    />
                    Reference Number
                    <span className="ml-2 text-xs font-normal text-gray-500">(Auto-generated)</span>
                  </label>
                  <div className="relative max-w-xs">
                    <input
                      type="text"
                      name="reference_number"
                      value={formData.reference_number || ''}
                      readOnly
                      className="input-field bg-gradient-to-r from-gray-50 to-gray-100 border-gray-300 text-gray-700 font-mono font-semibold cursor-not-allowed w-full"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <span className="text-base" style={{ color: '#0a5fe0' }}>👤</span>
                    Client Name <span className="text-red-500">*</span>
                  </label>
                  <div className="max-w-xs relative">
                    <select
                      name="client_name"
                      value={formData.client_name || ''}
                      onChange={handleChange}
                      className="input-field focus:ring-2 focus:ring-teal-300/30 w-full appearance-none pr-10 cursor-pointer"
                      required
                    >
                      <option value="">Select Client</option>
                      {clients.map(client => (
                        <option key={client} value={client}>{client}</option>
                      ))}
                    </select>
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Client Reference
                  </label>
                  <div className="max-w-xs">
                    <input
                      type="text"
                      name="client_ref_no"
                      value={formData.client_ref_no || ''}
                      onChange={handleChange}
                      className="input-field focus:ring-2 focus:ring-teal-300/30 w-full"
                      placeholder="Enter client reference number"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Operator <span className="text-red-500">*</span>
                  </label>
                  <div className="max-w-xs">
                    <input
                      type="text"
                      name="operator"
                      value={formData.operator || ''}
                      onChange={handleChange}
                      className="input-field focus:ring-2 focus:ring-teal-300/30 w-full"
                      placeholder="Enter operator name"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <span className="text-base" style={{ color: '#0a5fe0' }}>📋</span>
                    Survey Type <span className="text-red-500">*</span>
                  </label>
                  <div className="max-w-xs relative">
                    <select
                      name="survey_type"
                      value={formData.survey_type || ''}
                      onChange={handleChange}
                      className="input-field focus:ring-2 focus:ring-teal-300/30 w-full appearance-none pr-10 cursor-pointer"
                      required
                    >
                      <option value="">Select Survey Type</option>
                      {surveyTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <span className="text-base text-teal-500">🚢</span>
                    Vessel Name <span className="text-red-500">*</span>
                  </label>
                  <div className="max-w-xs">
                    <input
                      type="text"
                      name="vessel_name"
                      value={formData.vessel_name || ''}
                      onChange={handleChange}
                      className="input-field focus:ring-2 focus:ring-teal-300/30 w-full"
                      placeholder="Enter vessel name"
                      required
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Location & Schedule & Notes Section */}
            <div>
              <div className="mb-4 flex items-center gap-2.5 border-b border-[#e5ebf5] pb-2">
                <span className="h-4 w-1 rounded-full bg-premium"></span>
                <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-800">Location & Schedule & Notes</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Column 1 */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <span className="text-base text-teal-500">🌍</span>
                    Country <span className="text-red-500">*</span>
                  </label>
                  <div className="max-w-xs relative">
                    <select
                      name="survey_country"
                      value={formData.survey_country || ''}
                      onChange={handleChange}
                      className="input-field focus:ring-2 focus:ring-teal-300/30 w-full appearance-none pr-10 cursor-pointer"
                      required
                    >
                      <option value="">Select Country</option>
                      {countries.map(country => (
                        <option key={country} value={country}>{country}</option>
                      ))}
                    </select>
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Column 2 */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <span className="text-base text-teal-500">⚓</span>
                    Port <span className="text-red-500">*</span>
                  </label>
                  <div className="max-w-xs relative">
                    <select
                      name="survey_port"
                      value={formData.survey_port || ''}
                      onChange={handleChange}
                      className="input-field focus:ring-2 focus:ring-teal-300/30 disabled:bg-gray-50 disabled:cursor-not-allowed w-full appearance-none pr-10 cursor-pointer"
                      required
                      disabled={!formData.survey_country}
                    >
                      <option value="">{formData.survey_country ? 'Select Port' : 'Select Country first'}</option>
                      {ports.map(port => (
                        <option key={port} value={port}>{port}</option>
                      ))}
                    </select>
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Column 3 - Notes spanning 2 rows */}
                <div className="space-y-2 row-span-2 flex flex-col">
                  <label className="block text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <span className="text-base text-teal-500">📝</span>
                    Notes
                  </label>
                  <div className="max-w-xs flex-1 flex flex-col">
                    <textarea
                      name="notes"
                      value={formData.notes || ''}
                      onChange={handleChange}
                      className="input-field focus:ring-2 focus:ring-teal-300/30 resize-none w-full flex-1"
                      rows={6}
                      placeholder="Enter any additional notes or comments..."
                    />
                  </div>
                </div>

                {/* Column 1 - Row 2 */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <span className="text-base text-teal-500">📅</span>
                    ETA <span className="text-red-500">*</span>
                  </label>
                  <div className="max-w-xs relative">
                    <CustomDateInput
                      name="eta"
                      id="eta-date-input"
                      value={formData.eta || ''}
                      onChange={handleChange}
                      className="input-field focus:ring-2 focus:ring-teal-300/30 w-full cursor-pointer pr-10"
                      required
                    />
                    <div 
                      className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer z-10"
                      onClick={(e) => {
                        e.stopPropagation()
                        const input = document.getElementById('eta-date-input')
                        if (input && typeof input.showPicker === 'function') {
                          input.showPicker()
                        } else {
                          input?.click()
                        }
                      }}
                    >
                      <svg 
                        className="w-6 h-6 transition-transform hover:scale-110" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" fill="#0a5fe0" stroke="#0a5fe0" strokeWidth="1.5"/>
                        <line x1="8" y1="2" x2="8" y2="6" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                        <line x1="16" y1="2" x2="16" y2="6" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                        <line x1="3" y1="10" x2="21" y2="10" stroke="#fff" strokeWidth="1.5"/>
                        <circle cx="9" cy="14" r="1" fill="#fff"/>
                        <circle cx="12" cy="14" r="1" fill="#fff"/>
                        <circle cx="15" cy="14" r="1" fill="#fff"/>
                        <circle cx="9" cy="17" r="1" fill="#fff"/>
                        <circle cx="12" cy="17" r="1" fill="#fff"/>
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Column 2 - Row 2 */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <span className="text-base text-teal-500">📅</span>
                    Appointment Date <span className="text-red-500">*</span>
                  </label>
                  <div className="max-w-xs relative">
                    <CustomDateInput
                      name="appointment_date"
                      id="appointment-date-input"
                      value={formData.appointment_date || ''}
                      onChange={handleChange}
                      className="input-field focus:ring-2 focus:ring-teal-300/30 w-full cursor-pointer pr-10"
                      required
                    />
                    <div 
                      className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer z-10"
                      onClick={(e) => {
                        e.stopPropagation()
                        const input = document.getElementById('appointment-date-input')
                        if (input && typeof input.showPicker === 'function') {
                          input.showPicker()
                        } else {
                          input?.click()
                        }
                      }}
                    >
                      <svg 
                        className="w-6 h-6 transition-transform hover:scale-110" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" fill="#0a5fe0" stroke="#0a5fe0" strokeWidth="1.5"/>
                        <line x1="8" y1="2" x2="8" y2="6" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                        <line x1="16" y1="2" x2="16" y2="6" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                        <line x1="3" y1="10" x2="21" y2="10" stroke="#fff" strokeWidth="1.5"/>
                        <circle cx="9" cy="14" r="1" fill="#fff"/>
                        <circle cx="12" cy="14" r="1" fill="#fff"/>
                        <circle cx="15" cy="14" r="1" fill="#fff"/>
                        <circle cx="9" cy="17" r="1" fill="#fff"/>
                        <circle cx="12" cy="17" r="1" fill="#fff"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Surveyor Details Section */}
            <div>
              <div className="mb-4 flex items-center gap-2.5 border-b border-[#e5ebf5] pb-2">
                <span className="h-4 w-1 rounded-full bg-premium"></span>
                <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-800">Surveyor Details</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Surveyor Type
                  </label>
                  <div className="max-w-xs relative">
                    <select
                      name="surveyor_type"
                      value={formData.surveyor_type || ''}
                      onChange={handleChange}
                      className="input-field focus:ring-2 focus:ring-teal-300/30 w-full appearance-none pr-10 cursor-pointer"
                    >
                      <option value="">Select Surveyor Type</option>
                      <option value="In-House">In-House</option>
                      <option value="Subcont">Subcont</option>
                    </select>
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <span className="text-base" style={{ color: '#0a5fe0' }}>👤</span>
                    Surveyor
                  </label>
                  <div className="max-w-xs relative">
                    <select
                      name="surveyor"
                      value={formData.surveyor || ''}
                      onChange={handleChange}
                      className="input-field focus:ring-2 focus:ring-teal-300/30 disabled:bg-gray-50 disabled:cursor-not-allowed w-full appearance-none pr-10 cursor-pointer"
                      disabled={!formData.surveyor_type}
                    >
                      <option value="">{formData.surveyor_type ? 'Select Surveyor' : 'Select Surveyor Type first'}</option>
                      {surveyors.map(surveyor => (
                        <option key={surveyor.code} value={surveyor.code}>
                          {surveyor.name}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Operation Dates Section - Only shown in Edit Mode */}
            {id && (
              <div>
                <div className="mb-4 flex items-center gap-2.5 border-b border-[#e5ebf5] pb-2">
                  <span className="h-4 w-1 rounded-full bg-premium"></span>
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-800">Operation Dates</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <span className="text-base text-teal-500">📅</span>
                      Attendance Date
                    </label>
                    <div className="max-w-xs relative">
                      <CustomDateInput
                        name="attendance_date"
                        id="attendance-date-input"
                        value={formData.attendance_date || ''}
                        onChange={handleChange}
                        className="input-field focus:ring-2 focus:ring-teal-300/30 w-full cursor-pointer pr-10"
                      />
                      <div 
                        className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer z-10"
                        onClick={(e) => {
                          e.stopPropagation()
                          const input = document.getElementById('attendance-date-input')
                          if (input && typeof input.showPicker === 'function') {
                            input.showPicker()
                          } else {
                            input?.click()
                          }
                        }}
                      >
                        <svg 
                          className="w-6 h-6 transition-transform hover:scale-110" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" fill="#0a5fe0" stroke="#0a5fe0" strokeWidth="1.5"/>
                          <line x1="8" y1="2" x2="8" y2="6" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                          <line x1="16" y1="2" x2="16" y2="6" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                          <line x1="3" y1="10" x2="21" y2="10" stroke="#fff" strokeWidth="1.5"/>
                          <circle cx="9" cy="14" r="1" fill="#fff"/>
                          <circle cx="12" cy="14" r="1" fill="#fff"/>
                          <circle cx="15" cy="14" r="1" fill="#fff"/>
                          <circle cx="9" cy="17" r="1" fill="#fff"/>
                          <circle cx="12" cy="17" r="1" fill="#fff"/>
                        </svg>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <span className="text-base text-teal-500">📅</span>
                      Due Date of Payment
                    </label>
                    <div className="max-w-xs relative">
                      <CustomDateInput
                        name="due_date_payment"
                        id="due-date-payment-input"
                        value={formData.due_date_payment || ''}
                        onChange={handleChange}
                        className="input-field focus:ring-2 focus:ring-teal-300/30 w-full cursor-pointer pr-10"
                      />
                      <div 
                        className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer z-10"
                        onClick={(e) => {
                          e.stopPropagation()
                          const input = document.getElementById('due-date-payment-input')
                          if (input && typeof input.showPicker === 'function') {
                            input.showPicker()
                          } else {
                            input?.click()
                          }
                        }}
                      >
                        <svg 
                          className="w-6 h-6 transition-transform hover:scale-110" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" fill="#0a5fe0" stroke="#0a5fe0" strokeWidth="1.5"/>
                          <line x1="8" y1="2" x2="8" y2="6" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                          <line x1="16" y1="2" x2="16" y2="6" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                          <line x1="3" y1="10" x2="21" y2="10" stroke="#fff" strokeWidth="1.5"/>
                          <circle cx="9" cy="14" r="1" fill="#fff"/>
                          <circle cx="12" cy="14" r="1" fill="#fff"/>
                          <circle cx="15" cy="14" r="1" fill="#fff"/>
                          <circle cx="9" cy="17" r="1" fill="#fff"/>
                          <circle cx="12" cy="17" r="1" fill="#fff"/>
                        </svg>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <span className="text-base text-teal-500">📅</span>
                      Received Payment Date
                    </label>
                    <div className="max-w-xs relative">
                      {hasFullAccess ? (
                        <>
                          <CustomDateInput
                            name="received_payment_date"
                            id="received-payment-date-input"
                            value={formData.received_payment_date || ''}
                            onChange={handleChange}
                            className="input-field focus:ring-2 focus:ring-teal-300/30 w-full cursor-pointer pr-10"
                          />
                          <div 
                            className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer z-10"
                            onClick={(e) => {
                              e.stopPropagation()
                              const input = document.getElementById('received-payment-date-input')
                              if (input && typeof input.showPicker === 'function') {
                                input.showPicker()
                              } else {
                                input?.click()
                              }
                            }}
                          >
                            <svg 
                              className="w-6 h-6 transition-transform hover:scale-110" 
                              viewBox="0 0 24 24" 
                              fill="none" 
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" fill="#0a5fe0" stroke="#0a5fe0" strokeWidth="1.5"/>
                              <line x1="8" y1="2" x2="8" y2="6" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                              <line x1="16" y1="2" x2="16" y2="6" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                              <line x1="3" y1="10" x2="21" y2="10" stroke="#fff" strokeWidth="1.5"/>
                              <circle cx="9" cy="14" r="1" fill="#fff"/>
                              <circle cx="12" cy="14" r="1" fill="#fff"/>
                              <circle cx="15" cy="14" r="1" fill="#fff"/>
                              <circle cx="9" cy="17" r="1" fill="#fff"/>
                              <circle cx="12" cy="17" r="1" fill="#fff"/>
                            </svg>
                          </div>
                        </>
                      ) : (
                        <input
                          type="text"
                          value="-"
                          readOnly
                          disabled
                          className="input-field bg-gray-100 text-gray-500 cursor-not-allowed w-full"
                        />
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <span className="text-base text-teal-500">📅</span>
                      Initial Report Sent Date
                    </label>
                    <div className="max-w-xs relative">
                      <CustomDateInput
                        name="initial_report_sent_date"
                        id="initial-report-sent-date-input"
                        value={formData.initial_report_sent_date || ''}
                        onChange={handleChange}
                        className="input-field focus:ring-2 focus:ring-teal-300/30 w-full cursor-pointer pr-10"
                      />
                      <div 
                        className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer z-10"
                        onClick={(e) => {
                          e.stopPropagation()
                          const input = document.getElementById('initial-report-sent-date-input')
                          if (input && typeof input.showPicker === 'function') {
                            input.showPicker()
                          } else {
                            input?.click()
                          }
                        }}
                      >
                        <svg 
                          className="w-6 h-6 transition-transform hover:scale-110" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" fill="#0a5fe0" stroke="#0a5fe0" strokeWidth="1.5"/>
                          <line x1="8" y1="2" x2="8" y2="6" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                          <line x1="16" y1="2" x2="16" y2="6" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                          <line x1="3" y1="10" x2="21" y2="10" stroke="#fff" strokeWidth="1.5"/>
                          <circle cx="9" cy="14" r="1" fill="#fff"/>
                          <circle cx="12" cy="14" r="1" fill="#fff"/>
                          <circle cx="15" cy="14" r="1" fill="#fff"/>
                          <circle cx="9" cy="17" r="1" fill="#fff"/>
                          <circle cx="12" cy="17" r="1" fill="#fff"/>
                        </svg>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <span className="text-base text-teal-500">📅</span>
                      Final Report Sent Date
                    </label>
                    <div className="max-w-xs relative">
                      <CustomDateInput
                        name="final_report_sent_date"
                        id="final-report-sent-date-input"
                        value={formData.final_report_sent_date || ''}
                        onChange={handleChange}
                        className="input-field focus:ring-2 focus:ring-teal-300/30 w-full cursor-pointer pr-10"
                      />
                      <div 
                        className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer z-10"
                        onClick={(e) => {
                          e.stopPropagation()
                          const input = document.getElementById('final-report-sent-date-input')
                          if (input && typeof input.showPicker === 'function') {
                            input.showPicker()
                          } else {
                            input?.click()
                          }
                        }}
                      >
                        <svg 
                          className="w-6 h-6 transition-transform hover:scale-110" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" fill="#0a5fe0" stroke="#0a5fe0" strokeWidth="1.5"/>
                          <line x1="8" y1="2" x2="8" y2="6" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                          <line x1="16" y1="2" x2="16" y2="6" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                          <line x1="3" y1="10" x2="21" y2="10" stroke="#fff" strokeWidth="1.5"/>
                          <circle cx="9" cy="14" r="1" fill="#fff"/>
                          <circle cx="12" cy="14" r="1" fill="#fff"/>
                          <circle cx="15" cy="14" r="1" fill="#fff"/>
                          <circle cx="9" cy="17" r="1" fill="#fff"/>
                          <circle cx="12" cy="17" r="1" fill="#fff"/>
                        </svg>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            )}

            {/* Bunker Information Section - Only shown in Edit Mode */}
            {id && (
              <div>
                <div className="mb-4 flex items-center gap-2.5 border-b border-[#e5ebf5] pb-2">
                  <span className="h-4 w-1 rounded-full bg-premium"></span>
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-800">Bunker Information</h2>
                </div>
                <div className="overflow-x-auto shadow-lg rounded-xl border border-gray-200 bg-white" style={{ scrollbarWidth: 'auto', scrollbarColor: '#94a3b8 #f1f5f9' }}>
                  <style>{`
                    div::-webkit-scrollbar {
                      height: 12px;
                    }
                    div::-webkit-scrollbar-track {
                      background: #f1f5f9;
                      border-radius: 6px;
                    }
                    div::-webkit-scrollbar-thumb {
                      background: #94a3b8;
                      border-radius: 6px;
                    }
                    div::-webkit-scrollbar-thumb:hover {
                      background: #64748b;
                    }
                    /* Remove number input spinners */
                    input[type="number"]::-webkit-inner-spin-button,
                    input[type="number"]::-webkit-outer-spin-button {
                      -webkit-appearance: none;
                      margin: 0;
                    }
                    input[type="number"] {
                      -moz-appearance: textfield;
                    }
                  `}</style>
                  <table className="divide-y divide-gray-200" style={{ minWidth: '1400px', tableLayout: 'fixed', width: '100%' }}>
                    <thead className="table-head-premium">
                      <tr>
                        <th className="px-3 py-2.5 text-center text-[11px] font-semibold text-white uppercase tracking-wider border-r border-white/20" style={{ width: hasFullAccess ? '9.09%' : '16.67%' }}>Fuel Type</th>
                        <th className="px-5 py-2.5 text-center text-[11px] font-semibold text-white uppercase tracking-wider border-r border-white/20" style={{ width: hasFullAccess ? '9.09%' : '16.67%' }}>
                          <div className="flex flex-col items-center">
                            <span>Logbook</span>
                            <span className="text-[10px] font-normal normal-case opacity-80">(MT)</span>
                          </div>
                        </th>
                        <th className="px-5 py-2.5 text-center text-[11px] font-semibold text-white uppercase tracking-wider border-r border-white/20" style={{ width: hasFullAccess ? '9.09%' : '16.67%' }}>
                          <div className="flex flex-col items-center">
                            <span>Survey</span>
                            <span className="text-[10px] font-normal normal-case opacity-80">(MT)</span>
                          </div>
                        </th>
                        <th className="px-5 py-2.5 text-center text-[11px] font-semibold text-white uppercase tracking-wider border-r border-white/20" style={{ width: hasFullAccess ? '9.09%' : '16.67%' }}>
                          <div className="flex flex-col items-center">
                            <span>Difference</span>
                            <span className="text-[10px] font-normal normal-case opacity-80">(MT)</span>
                          </div>
                        </th>
                        <th className="px-5 py-2.5 text-center text-[11px] font-semibold text-white uppercase tracking-wider border-r border-white/20" style={{ width: hasFullAccess ? '9.09%' : '16.67%' }}>
                          <div className="flex flex-col items-center">
                            <span className="whitespace-nowrap">BDN Fig.</span>
                            <span className="text-[10px] font-normal normal-case opacity-80">(MT)</span>
                          </div>
                        </th>
                        <th className="px-5 py-2.5 text-center text-[11px] font-semibold text-white uppercase tracking-wider border-r border-white/20" style={{ width: hasFullAccess ? '9.09%' : '16.67%' }}>
                          <div className="flex flex-col items-center">
                            <span className="whitespace-nowrap">Vessel Supplied</span>
                            <span className="text-[10px] font-normal normal-case opacity-80">(MT)</span>
                          </div>
                        </th>
                        {hasFullAccess && (
                          <>
                            <th className="px-5 py-2.5 text-center text-[11px] font-semibold text-white uppercase tracking-wider border-r border-white/20" style={{ width: '9.09%' }}>
                              <div className="flex flex-col items-center gap-1">
                                <span>Barge Name</span>
                              </div>
                            </th>
                            <th className="px-5 py-2.5 text-center text-[11px] font-semibold text-white uppercase tracking-wider border-r border-white/20" style={{ width: '9.09%' }}>
                              <div className="flex flex-col items-center gap-1">
                                <span>Fuel Price</span>
                                <svg className="w-4 h-4 text-emerald-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </div>
                            </th>
                            <th className="px-5 py-2.5 text-center text-[11px] font-semibold text-white uppercase tracking-wider border-r border-white/20" style={{ width: '9.09%' }}>
                              <div className="flex flex-col items-center gap-1">
                                <span>Recovery</span>
                                <svg className="w-4 h-4 text-emerald-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </div>
                            </th>
                            <th className="px-5 py-2.5 text-center text-[11px] font-semibold text-white uppercase tracking-wider border-r border-white/20" style={{ width: '9.09%' }}>
                              <div className="flex flex-col items-center gap-1">
                                <span>PCT Incentive</span>
                                <span className="text-xs font-normal opacity-90">(%)</span>
                              </div>
                            </th>
                            <th className="px-5 py-2.5 text-center text-[11px] font-semibold text-white uppercase tracking-wider" style={{ width: '9.09%' }}>
                              <div className="flex flex-col items-center gap-1">
                                <span>MT Price</span>
                                <svg className="w-4 h-4 text-emerald-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </div>
                            </th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {/* LSFO Row */}
                      <tr className="hover:bg-emerald-50/30 transition-colors duration-150">
                        <td className="px-3 py-4 whitespace-nowrap text-center border-r border-gray-200" style={{ width: hasFullAccess ? '9.09%' : '16.67%' }}>
                          <span className="inline-flex items-center px-2 py-1.5 rounded-md text-sm font-bold text-white shadow-sm" style={{ background: 'linear-gradient(to right, #0a5fe0, #0a4fd4)' }}>
                            LSFO
                          </span>
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap text-center border-r border-gray-200">
                          <input
                            type="number"
                            step="0.001"
                            name="lsfo_logbook"
                            value={formData.lsfo_logbook || '0.000'}
                            onChange={handleChange}
                            className="w-full px-3 py-2 text-sm text-center border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 hover:border-gray-400 transition-colors"
                          />
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap text-center border-r border-gray-200">
                          <input
                            type="number"
                            step="0.001"
                            name="lsfo_survey"
                            value={formData.lsfo_survey || '0.000'}
                            onChange={handleChange}
                            className="w-full px-3 py-2 text-sm text-center border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 hover:border-gray-400 transition-colors"
                          />
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap text-center border-r border-gray-200">
                          <input
                            type="number"
                            step="0.001"
                            name="lsfo_qty"
                            value={formData.lsfo_qty || '0.000'}
                            readOnly
                            className="w-full px-3 py-2 text-sm text-center border border-gray-300 rounded-md bg-gray-50 text-gray-700 font-medium cursor-not-allowed"
                          />
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap text-center border-r border-gray-200">
                          <input
                            type="number"
                            step="0.001"
                            name="lsfo_bdn"
                            value={formData.lsfo_bdn || '0.000'}
                            onChange={handleChange}
                            className="w-full px-3 py-2 text-sm text-center border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 hover:border-gray-400 transition-colors"
                          />
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap text-center border-r border-gray-200">
                          <input
                            type="number"
                            step="0.001"
                            name="lsfo_vessel"
                            value={formData.lsfo_vessel || '0.000'}
                            onChange={handleChange}
                            className="w-full px-3 py-2 text-sm text-center border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 hover:border-gray-400 transition-colors"
                          />
                        </td>
                        {hasFullAccess && (
                          <>
                            <td className="px-5 py-4 whitespace-nowrap text-center border-r border-gray-200">
                              <input
                                type="text"
                                name="lsfo_barge_name"
                                value={formData.lsfo_barge_name || ''}
                                onChange={handleChange}
                                className="w-full px-3 py-2 text-sm text-center border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 hover:border-gray-400 transition-colors"
                                placeholder="Enter barge name"
                              />
                            </td>
                            <td className="px-5 py-4 whitespace-nowrap text-center border-r border-gray-200">
                              <input
                                type="number"
                                step="0.01"
                                name="lsfo_price"
                                value={formData.lsfo_price || '0.00'}
                                onChange={handleChange}
                                className="w-full px-3 py-2 text-sm text-center border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 hover:border-gray-400 transition-colors"
                              />
                            </td>
                            <td className="px-5 py-4 whitespace-nowrap text-center border-r border-gray-200">
                              <input
                                type="number"
                                step="0.01"
                                name="lsfo_recovery"
                                value={formData.lsfo_recovery || '0.00'}
                                readOnly
                                className="w-full px-3 py-2 text-sm text-center border border-gray-300 rounded-md bg-[#eef4ff] text-gray-700 font-semibold cursor-not-allowed"
                              />
                            </td>
                            <td className="px-5 py-4 whitespace-nowrap text-center border-r border-gray-200 align-middle" rowSpan={3}>
                              <input
                                type="number"
                                step="0.01"
                                name="pct_qty"
                                value={formData.pct_qty || ''}
                                onChange={handleChange}
                                disabled={pctDisabled}
                                className={`w-full px-3 py-2 text-sm text-center border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors ${
                                  pctDisabled 
                                    ? 'bg-gray-100 text-gray-500 cursor-not-allowed' 
                                    : 'bg-white hover:border-gray-400'
                                }`}
                              />
                            </td>
                            <td className="px-5 py-4 whitespace-nowrap text-center align-middle" rowSpan={3}>
                              <input
                                type="number"
                                step="0.01"
                                name="mt_price"
                                value={formData.mt_price || ''}
                                onChange={handleChange}
                                disabled={mtPriceDisabled}
                                className={`w-full px-3 py-2 text-sm text-center border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors ${
                                  mtPriceDisabled 
                                    ? 'bg-gray-100 text-gray-500 cursor-not-allowed' 
                                    : 'bg-white hover:border-gray-400'
                                }`}
                              />
                            </td>
                          </>
                        )}
                      </tr>
                      {/* HSFO Row */}
                      <tr className="hover:bg-cyan-50/30 transition-colors duration-150">
                        <td className="px-3 py-4 whitespace-nowrap text-center border-r border-gray-200" style={{ width: hasFullAccess ? '9.09%' : '16.67%' }}>
                          <span className="inline-flex items-center px-2 py-1.5 rounded-md text-sm font-bold text-white bg-gradient-to-r from-cyan-500 to-cyan-400 shadow-sm">
                            HSFO
                          </span>
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap text-center border-r border-gray-200">
                          <input
                            type="number"
                            step="0.001"
                            name="hsfo_logbook"
                            value={formData.hsfo_logbook || '0.000'}
                            onChange={handleChange}
                            className="w-full px-3 py-2 text-sm text-center border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 hover:border-gray-400 transition-colors"
                          />
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap text-center border-r border-gray-200">
                          <input
                            type="number"
                            step="0.001"
                            name="hsfo_survey"
                            value={formData.hsfo_survey || '0.000'}
                            onChange={handleChange}
                            className="w-full px-3 py-2 text-sm text-center border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 hover:border-gray-400 transition-colors"
                          />
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap text-center border-r border-gray-200">
                          <input
                            type="number"
                            step="0.001"
                            name="hsfo_qty"
                            value={formData.hsfo_qty || '0.000'}
                            readOnly
                            className="w-full px-3 py-2 text-sm text-center border border-gray-300 rounded-md bg-gray-50 text-gray-700 font-medium cursor-not-allowed"
                          />
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap text-center border-r border-gray-200">
                          <input
                            type="number"
                            step="0.001"
                            name="hsfo_bdn"
                            value={formData.hsfo_bdn || '0.000'}
                            onChange={handleChange}
                            className="w-full px-3 py-2 text-sm text-center border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 hover:border-gray-400 transition-colors"
                          />
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap text-center border-r border-gray-200">
                          <input
                            type="number"
                            step="0.001"
                            name="hsfo_vessel"
                            value={formData.hsfo_vessel || '0.000'}
                            onChange={handleChange}
                            className="w-full px-3 py-2 text-sm text-center border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 hover:border-gray-400 transition-colors"
                          />
                        </td>
                        {hasFullAccess && (
                          <>
                            <td className="px-5 py-4 whitespace-nowrap text-center border-r border-gray-200">
                              <input
                                type="text"
                                name="hsfo_barge_name"
                                value={formData.hsfo_barge_name || ''}
                                onChange={handleChange}
                                className="w-full px-3 py-2 text-sm text-center border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 hover:border-gray-400 transition-colors"
                                placeholder="Enter barge name"
                              />
                            </td>
                            <td className="px-5 py-4 whitespace-nowrap text-center border-r border-gray-200">
                              <input
                                type="number"
                                step="0.01"
                                name="hsfo_price"
                                value={formData.hsfo_price || '0.00'}
                                onChange={handleChange}
                                className="w-full px-3 py-2 text-sm text-center border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 hover:border-gray-400 transition-colors"
                              />
                            </td>
                            <td className="px-5 py-4 whitespace-nowrap text-center border-r border-gray-200">
                              <input
                                type="number"
                                step="0.01"
                                name="hsfo_recovery"
                                value={formData.hsfo_recovery || '0.00'}
                                readOnly
                                className="w-full px-3 py-2 text-sm text-center border border-gray-300 rounded-md bg-[#eef4ff] text-gray-700 font-semibold cursor-not-allowed"
                              />
                            </td>
                          </>
                        )}
                      </tr>
                      {/* MGO Row */}
                      <tr className="hover:bg-emerald-50/30 transition-colors duration-150">
                        <td className="px-3 py-4 whitespace-nowrap text-center border-r border-gray-200" style={{ width: hasFullAccess ? '9.09%' : '16.67%' }}>
                          <span className="inline-flex items-center px-2 py-1.5 rounded-md text-sm font-bold text-white bg-gradient-to-r from-emerald-500 to-emerald-400 shadow-sm">
                            MGO
                          </span>
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap text-center border-r border-gray-200">
                          <input
                            type="number"
                            step="0.001"
                            name="mgo_logbook"
                            value={formData.mgo_logbook || '0.000'}
                            onChange={handleChange}
                            className="w-full px-3 py-2 text-sm text-center border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 hover:border-gray-400 transition-colors"
                          />
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap text-center border-r border-gray-200">
                          <input
                            type="number"
                            step="0.001"
                            name="mgo_survey"
                            value={formData.mgo_survey || '0.000'}
                            onChange={handleChange}
                            className="w-full px-3 py-2 text-sm text-center border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 hover:border-gray-400 transition-colors"
                          />
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap text-center border-r border-gray-200">
                          <input
                            type="number"
                            step="0.001"
                            name="mgo_qty"
                            value={formData.mgo_qty || '0.000'}
                            readOnly
                            className="w-full px-3 py-2 text-sm text-center border border-gray-300 rounded-md bg-gray-50 text-gray-700 font-medium cursor-not-allowed"
                          />
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap text-center border-r border-gray-200">
                          <input
                            type="number"
                            step="0.001"
                            name="mgo_bdn"
                            value={formData.mgo_bdn || '0.000'}
                            onChange={handleChange}
                            className="w-full px-3 py-2 text-sm text-center border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 hover:border-gray-400 transition-colors"
                          />
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap text-center border-r border-gray-200">
                          <input
                            type="number"
                            step="0.001"
                            name="mgo_vessel"
                            value={formData.mgo_vessel || '0.000'}
                            onChange={handleChange}
                            className="w-full px-3 py-2 text-sm text-center border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 hover:border-gray-400 transition-colors"
                          />
                        </td>
                        {hasFullAccess && (
                          <>
                            <td className="px-5 py-4 whitespace-nowrap text-center border-r border-gray-200">
                              <input
                                type="text"
                                name="mgo_barge_name"
                                value={formData.mgo_barge_name || ''}
                                onChange={handleChange}
                                className="w-full px-3 py-2 text-sm text-center border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 hover:border-gray-400 transition-colors"
                                placeholder="Enter barge name"
                              />
                            </td>
                            <td className="px-5 py-4 whitespace-nowrap text-center border-r border-gray-200">
                              <input
                                type="number"
                                step="0.01"
                                name="mgo_price"
                                value={formData.mgo_price || '0.00'}
                                onChange={handleChange}
                                className="w-full px-3 py-2 text-sm text-center border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 hover:border-gray-400 transition-colors"
                              />
                            </td>
                            <td className="px-5 py-4 whitespace-nowrap text-center border-r border-gray-200">
                              <input
                                type="number"
                                step="0.01"
                                name="mgo_recovery"
                                value={formData.mgo_recovery || '0.00'}
                                readOnly
                                className="w-full px-3 py-2 text-sm text-center border border-gray-300 rounded-md bg-[#eef4ff] text-gray-700 font-semibold cursor-not-allowed"
                              />
                            </td>
                          </>
                        )}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Financial Details Section - Only shown in Edit Mode for Superusers and Admins */}
            {id && hasFullAccess && (
              <div>
                <div className="flex items-center justify-between gap-2 mb-6 py-3 px-4 rounded-lg bg-gradient-to-r from-teal-50 via-teal-50/80 to-transparent border-l-4 border-teal-400 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-1 h-8 rounded-full" style={{ background: 'linear-gradient(to bottom, #0a5fe0, #0a4fd4)' }}></div>
                    <h2 className="text-xl font-semibold text-gray-800">Financial Details</h2>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="block text-sm font-semibold text-gray-700">
                      Subcon Fee
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        name="subcon"
                        value={formData.subcon || ''}
                        onChange={handleChange}
                        className="input-field focus:ring-2 focus:ring-teal-300/30 w-40"
                        placeholder="Enter subcon fee"
                      />
                      <div className="flex items-center gap-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            name="subcon_fee_paid"
                            checked={formData.subcon_fee_paid || false}
                            onChange={(e) => setFormData(prev => ({ ...prev, subcon_fee_paid: e.target.checked }))}
                            className="w-5 h-5 text-teal-500 rounded focus:ring-teal-300"
                          />
                          <span className="text-sm text-gray-700">Paid</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <svg className="w-5 h-5 text-[#0a5fe0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Incentive
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      name="recovery"
                      value={formData.recovery || ''}
                      onChange={handleChange}
                      className="input-field focus:ring-2 focus:ring-teal-300/30 w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <svg className="w-5 h-5 text-[#0a5fe0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Survey Fee
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      name="survey_fee"
                      value={formData.survey_fee || ''}
                      onChange={handleChange}
                      className="input-field focus:ring-2 focus:ring-teal-300/30 w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <svg className="w-5 h-5 text-[#0a5fe0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Expenses
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      name="expenses"
                      value={formData.expenses || ''}
                      onChange={handleChange}
                      className="input-field focus:ring-2 focus:ring-teal-300/30 w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <svg className="w-5 h-5 text-[#0a5fe0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Discount
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      name="discount"
                      value={formData.discount || ''}
                      onChange={handleChange}
                      className="input-field focus:ring-2 focus:ring-teal-300/30 w-full"
                    />
                  </div>
                  <div className="space-y-3 lg:col-span-3 rounded-lg border border-teal-100 bg-teal-50/40 p-4">
                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700 flex items-center gap-2">
                        <svg className="w-5 h-5 text-[#0a5fe0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Others
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        name="others"
                        title="Others is the sum of sub-lines below. Use Add to create breakdown lines."
                        value={sumOthersLineAmounts(othersLines).toFixed(2)}
                        disabled
                        className="input-field focus:ring-2 focus:ring-teal-300/30 w-full disabled:bg-gray-100 disabled:text-gray-700"
                      />
                      <p className="text-xs text-gray-600 leading-snug">
                        Sub-lines match the invoice Others breakdown; the total updates when you add or edit lines here or on the invoice.
                      </p>
                    </div>
                    <div className="pl-3 border-l-2 border-teal-200 space-y-2">
                      {othersLines.map((row) => (
                        <div key={row.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-3 items-center">
                          <div className="md:col-span-2 hidden md:block" aria-hidden />
                          <input
                            type="text"
                            className="md:col-span-5 input-field focus:ring-2 focus:ring-teal-300/30 text-sm"
                            placeholder="Description"
                            value={row.description}
                            onChange={(e) => updateOthersLine(row.id, 'description', e.target.value)}
                          />
                          <input
                            type="number"
                            step="0.01"
                            className="md:col-span-3 input-field focus:ring-2 focus:ring-teal-300/30 text-sm text-right"
                            value={row.amount}
                            onChange={(e) => updateOthersLine(row.id, 'amount', e.target.value)}
                          />
                          <div className="md:col-span-2 flex justify-end md:justify-start">
                            <button
                              type="button"
                              aria-label="Remove breakdown line"
                              className="min-w-[2.75rem] px-3 py-2 text-sm rounded-md border border-red-200 bg-red-50 font-semibold text-red-700 hover:bg-red-100"
                              onClick={() => removeOthersSurveyLine(row.id)}
                            >
                              X
                            </button>
                          </div>
                        </div>
                      ))}
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-3 items-center">
                        <div className="hidden md:block md:col-span-2" aria-hidden />
                        <div className="hidden md:block md:col-span-5" aria-hidden />
                        <div className="hidden md:block md:col-span-3" aria-hidden />
                        <div className="md:col-span-2 flex justify-end md:justify-start">
                          <button
                            type="button"
                            onClick={addOthersSurveyLine}
                            className="min-w-[5rem] inline-flex items-center justify-center px-3 py-2 rounded-md text-sm font-semibold bg-gradient-to-r from-teal-500 to-teal-600 text-white hover:from-teal-600 hover:to-teal-700 shadow-sm"
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <svg className="w-5 h-5 text-[#0a5fe0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Total
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      name="total"
                      value={formData.total || ''}
                      readOnly
                      className="input-field bg-gray-100 text-gray-700 font-semibold w-full"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Form Actions Footer */}
          <div className="bg-gray-50 border-t border-gray-200 px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-gray-500">
              <span className="text-red-500">*</span> Required fields
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => navigate('/surveys')}
                className="px-6 py-2.5 bg-white hover:bg-gray-50 text-gray-700 border-2 border-gray-300 hover:border-gray-400 font-semibold rounded-xl shadow-sm hover:shadow-md transition-all duration-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-8 py-2.5 bg-premium text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Save Survey</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default SurveyForm

